import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Navigate, useNavigate } from "react-router";
import { useAuth } from "../lib/auth.tsx";
import { tenantsApi } from "../api/client.ts";
import { AuthShell, Field } from "./Login.tsx";

const FormSchema = z.object({
  name: z
    .string()
    .min(1, "Required")
    .max(120, "Too long"),
  phone: z
    .string()
    .min(7, "Too short")
    .max(20, "Too long")
    .optional()
    .or(z.literal("").transform(() => undefined)),
});
type FormValues = z.infer<typeof FormSchema>;

/**
 * First-time tenant provisioning.
 *
 * Cognito users who haven't yet been linked to a tenants row arrive
 * here after sign-in (RequireTenant in App.tsx routes them in). They
 * provide a business name; the API creates the tenant row, patches
 * their `custom:tenantId` Cognito attribute, then we force-refresh
 * the session so the new claim shows up in the ID token, and route
 * them to the dashboard.
 */
export function OnboardingPage() {
  const { isLoading, isAuthenticated, profile, idToken, tenant, refresh } = useAuth();
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { name: "", phone: "" },
  });

  if (isLoading) {
    return (
      <div className="grid min-h-screen place-items-center text-sm text-slate-500">
        Loading…
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // Already provisioned — bounce to dashboard so refresh doesn't dead-end here.
  if (tenant) return <Navigate to="/" replace />;

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    if (!idToken) {
      setServerError("Not signed in. Please sign in again.");
      return;
    }
    try {
      await tenantsApi.create(idToken, {
        name: values.name,
        ...(values.phone ? { phone: values.phone } : {}),
      });
      // Force a token refresh so the new custom:tenantId claim is on the
      // ID token we'll send to the API from here on.
      await refresh();
      navigate("/", { replace: true });
    } catch (err) {
      setServerError(
        err instanceof Error ? err.message : "Couldn't create your account",
      );
    }
  });

  return (
    <AuthShell
      title="Set up your SpotMesh account"
      subtitle="One more step — tell us who you are."
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="Business or vendor name" error={errors.name?.message}>
          <input
            type="text"
            autoComplete="organization"
            placeholder="e.g. Kibanda WiFi Mlolongo"
            {...register("name")}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </Field>
        <Field
          label="Contact phone (optional)"
          error={errors.phone?.message}
        >
          <input
            type="tel"
            autoComplete="tel"
            placeholder="0712 345 678"
            {...register("phone")}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </Field>
        <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Signing up as{" "}
          <span className="font-medium text-slate-900">
            {profile?.email ?? "—"}
          </span>
        </div>

        {serverError && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
            {serverError}
          </p>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {isSubmitting ? "Setting up…" : "Continue to dashboard"}
        </button>
      </form>
    </AuthShell>
  );
}
